import type { StoredFormula } from './stored'
import { STORED_VERSION } from './stored'

/**
 * What a formula configuration IS, apart from the grid that renders it.
 *
 * These lived in `engine.ts`, which also builds the columns a React grid draws
 * and therefore imports the grid's own types. The Worker evaluates formulas
 * with this same engine — importing it rather than keeping a second
 * implementation that would silently drift — and a Worker has no business
 * pulling in a browser module chain to do it. So the config, the calculated
 * column and the `calc:` field naming live here, and `engine.ts` re-exports
 * them: every existing import keeps working.
 */

export interface FormulaColumn {
  /** Immutable id assigned at creation. The column's `field` is `calc:${id}`, so renaming the
   *  title cannot break the formulas that reference it. */
  id: string
  /** Visible name. `encabezados_grilla` can override it like any other column's. */
  title: string
  /** The formula, stored as structure rather than text — see `stored.ts`. */
  formula: StoredFormula
  /** `excelFormat` pattern for the result, canonical. Defaults to `DEFAULT_FORMAT` — never empty. */
  format?: string
  minWidth?: number
}

export interface GridFormulaConfig {
  columns: FormulaColumn[];
  /** Base column `field` → its stored formula. */
  overrides: Record<string, StoredFormula>;
  /** Stored shape version. 1 and 2 were canonical TEXT — v1 with `[field]` meaning the current
   * row, v2 spelling that `[@field]`. v3 is a structured AST (`stored.ts`), and `migrateV2.ts`
   * upgrades the older two on read. */
  syntaxVersion?: 1 | 2 | 3;
  /** Formula field → whether per-cell value/blank/formula exceptions are active.
   * Missing entries intentionally mean `true` for backwards compatibility. */
  allowCellOverrides?: Record<string, boolean>;
  /** Formula field → the field a manual entry back-solves instead of overriding the cell.
   * Absent (or empty) means «same column»: the typed value stays where it was typed, the
   * behaviour that predates goal seek. See `goalSeek.ts`. */
  overrideTargets?: Record<string, string>;
}

export const EMPTY_FORMULA_CONFIG: GridFormulaConfig = {
  columns: [],
  overrides: {},
  syntaxVersion: STORED_VERSION,
  allowCellOverrides: {},
  overrideTargets: {},
};

const CALC_PREFIX = 'calc:'

/** The `field` a calculated column uses to take part in the rest of the grid's config (widths,
 *  order, hidden, sort) exactly like any other column. */
export const formulaField = (id: string): string => `${CALC_PREFIX}${id}`

/** The calculated-column id behind a `field`, or `null` if the field is a real column's. */
export const formulaIdOf = (field: string): string | null =>
  field.startsWith(CALC_PREFIX) ? field.slice(CALC_PREFIX.length) : null

/** Whether `field` is driven by a formula in `config` — a calculated column, or a base column
 *  with an override. Used to decide which columns get the manual-override affordance (the
 *  corner triangle, editability) — see `cellOverrides.ts` and its wiring in `Grid.tsx`. */
export function isFormulaField(field: string, config: GridFormulaConfig): boolean {
  return formulaIdOf(field) !== null || field in config.overrides
}

export const DEFAULT_FORMAT = '#,##0.00'
